/**
 * <BulkCardUploadModal /> — Phase FC-E (Feb 2026)
 *
 * Customer-scoped Bulk Add Fuel Cards workflow. Two backend calls only:
 *   1. POST /customers/{id}/cards/bulk-preview   → dry-run validation
 *   2. POST /customers/{id}/cards/bulk-commit    → all-or-nothing commit
 *
 * Commit is guarded by:
 *   - Preview must have succeeded on the CURRENT file.
 *   - Preview must be actionable (can_commit && total_rows > 0 && invalid_rows === 0).
 *   - Explicit confirmation dialog with network counts + "no rematch" notice.
 *   - Double-submit lock while the request is in flight.
 *
 * Success path:
 *   - HTTP 200 with `phase === "FC-D"` triggers `onCommitted()` which the
 *     parent CustomerDetail uses to refresh the Fuel Cards tile.
 *   - Non-success paths (Cancel, 422, 409, 500, unknown 5xx, network error)
 *     never invoke `onCommitted`.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { downloadFromBackend } from "../lib/download";
import {
  computeCanCommit,
  computeNetworkCounts,
  classifyCommitError,
} from "./bulkCardCommitLogic";

const NETWORK_LABEL = {
  esso: "Esso",
  pipeline: "Pipeline",
  petro_pass: "Petro-Pass",
};

export default function BulkCardUploadModal({
  customerId,
  companyName,
  onClose,
  onCommitted,
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // last preview payload
  const [previewedFile, setPreviewedFile] = useState(null); // file that produced `preview`
  const [phase, setPhase] = useState("idle");     // idle|previewing|preview_ready|confirming|committing
  const [banner, setBanner] = useState(null);     // {tone: "info"|"warn"|"err"|"critical", text: string}
  const [downloadingTpl, setDownloadingTpl] = useState(false);

  const summary = preview?.summary || null;
  const canCommit = computeCanCommit({ preview, file, previewedFile, phase });

  // Confirmation dialog uses only networks that actually appear in the
  // preview (never invents ta_petro / Country / etc.).
  const networkCounts = useMemo(() => computeNetworkCounts(preview), [preview]);

  // ---- File selection — always invalidate the previous preview ----------
  const onFileChange = (e) => {
    const next = e.target.files?.[0] || null;
    setFile(next);
    setPreview(null);
    setPreviewedFile(null);
    setBanner(null);
    setPhase("idle");
  };

  // ---- Template download -----------------------------------------------
  const downloadTemplate = async () => {
    setDownloadingTpl(true);
    try {
      await downloadFromBackend(
        `/customers/${customerId}/cards/bulk-template.xlsx`,
        {},
      );
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    } finally {
      setDownloadingTpl(false);
    }
  };

  // ---- Preview submit ---------------------------------------------------
  const submitPreview = async () => {
    if (!file) {
      toast.error("Choose an .xlsx file first.");
      return;
    }
    setPhase("previewing");
    setBanner(null);
    setPreview(null);
    setPreviewedFile(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/customers/${customerId}/cards/bulk-preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
      setPreviewedFile(file);
      setPhase("preview_ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
      setPhase("idle");
    }
  };

  // ---- Commit flow ------------------------------------------------------
  const openConfirm = () => {
    if (!canCommit) return;
    setPhase("confirming");
    setBanner(null);
  };

  const closeConfirm = () => {
    if (phase === "committing") return;
    setPhase("preview_ready");
  };

  const submitCommit = async () => {
    // Same File object that produced the preview — the backend will
    // re-parse and re-validate. We never send preview JSON as source
    // of truth.
    if (!file || file !== previewedFile) return;
    setPhase("committing");
    setBanner(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await api.post(
        `/customers/${customerId}/cards/bulk-commit`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const data = resp?.data;
      if (resp?.status === 200 && data?.phase === "FC-D") {
        toast.success(`${data.committed} card${data.committed === 1 ? "" : "s"} created successfully.`);
        // Signal parent to refresh Fuel Cards tile via existing load().
        onCommitted?.();
        // Local cleanup (parent may also close via onCommitted; we
        // clear state regardless so a re-open starts fresh).
        setFile(null);
        setPreview(null);
        setPreviewedFile(null);
        setPhase("idle");
        return;
      }
      // Success with unexpected shape → treat as critical unknown.
      handleUnknown5xx(new Error("Unexpected commit response shape"));
    } catch (e) {
      handleCommitError(e);
    }
  };

  const handleCommitError = (e) => {
    const cls = classifyCommitError(e);
    if (cls.kind === "rejected") {
      // Preview the backend just re-computed becomes the new source of truth.
      const nextPreview = {
        customer_id: cls.updatedPreview.customer_id,
        company_name: preview?.company_name || companyName,
        phase: "FC-D",
        commit_enabled: false,
        summary: cls.updatedPreview.summary,
        rows: cls.updatedPreview.rows,
      };
      setPreview(nextPreview);
      setPreviewedFile(null); // require re-preview before commit
      setBanner({ tone: cls.tone, text: cls.text });
      setPhase("preview_ready");
      return;
    }
    if (cls.kind === "race") {
      setPreviewedFile(null);
      setBanner({ tone: cls.tone, text: cls.text });
      setPhase("preview_ready");
      return;
    }
    if (cls.kind === "rolled_back") {
      setPreviewedFile(null);
      setBanner({ tone: cls.tone, text: cls.text });
      setPhase("preview_ready");
      return;
    }
    // Unknown / critical 5xx / network error.
    setPreviewedFile(null);
    setBanner({ tone: cls.tone, text: cls.text, debugCode: cls.debugCode });
    setPhase("preview_ready");
  };

  const handleUnknown5xx = (e) => {
    const cls = classifyCommitError(e);
    setPreviewedFile(null);
    setBanner({ tone: cls.tone, text: cls.text, debugCode: cls.debugCode });
    setPhase("preview_ready");
  };

  const committing = phase === "committing";
  const confirming = phase === "confirming";
  const previewing = phase === "previewing";

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={() => !committing && !confirming && onClose()}
      data-testid="bulk-card-upload-modal"
    >
      <div
        className="bg-white w-full max-w-4xl border border-zinc-200 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gf-card-header">
          <h3>Bulk Add Fuel Cards</h3>
          <div
            className="overline"
            data-testid="bulk-card-modal-company"
          >
            {companyName || customerId}
          </div>
        </div>

        <div className="gf-card-body flex-1 overflow-y-auto space-y-4">
          {/* Optional response banner (422 / 409 / 500 / critical) */}
          {banner && (
            <div
              className={
                "text-xs p-2 rounded border " +
                (banner.tone === "critical"
                  ? "border-red-600 bg-red-50 text-red-900 font-semibold"
                  : banner.tone === "err"
                  ? "border-red-400 bg-red-50 text-red-900"
                  : banner.tone === "warn"
                  ? "border-amber-400 bg-amber-50 text-amber-900"
                  : "border-blue-300 bg-blue-50 text-blue-900")
              }
              data-testid={`bulk-card-banner-${banner.tone}`}
            >
              {banner.text}
              {banner.debugCode && (
                <span className="ml-2 opacity-70">
                  (ref: {banner.debugCode})
                </span>
              )}
            </div>
          )}

          {/* Step 1 — template */}
          <div className="border border-zinc-200 p-3">
            <div className="overline mb-1">Step 1 · Download template</div>
            <div className="text-xs text-zinc-600 mb-2">
              The template has Card Number pre-formatted as Text so leading
              zeros (e.g. Pipeline <code>0001</code>) survive Excel round-tripping.
              Do not add a Country or Customer column — the target customer
              is bound by this page.
            </div>
            <button
              type="button"
              className="gf-btn gf-btn-outline"
              onClick={downloadTemplate}
              disabled={downloadingTpl || committing}
              data-testid="bulk-card-download-template"
            >
              {downloadingTpl ? "Preparing…" : "Download .xlsx template"}
            </button>
          </div>

          {/* Step 2 — upload */}
          <div className="border border-zinc-200 p-3">
            <div className="overline mb-1">Step 2 · Upload for preview</div>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".xlsx"
                onChange={onFileChange}
                disabled={previewing || committing}
                data-testid="bulk-card-file-input"
              />
              <button
                type="button"
                className="gf-btn gf-btn-primary"
                onClick={submitPreview}
                disabled={!file || previewing || committing}
                data-testid="bulk-card-preview-submit"
              >
                {previewing ? "Validating…" : "Preview"}
              </button>
            </div>
            {file && !previewedFile && preview === null && (
              <div className="text-[11px] text-zinc-500 mt-2">
                Click <b>Preview</b> to validate this workbook.
              </div>
            )}
            {file && previewedFile && file !== previewedFile && (
              <div
                className="text-[11px] text-amber-700 mt-2"
                data-testid="bulk-card-stale-preview-hint"
              >
                File changed — previous preview cleared. Preview again before creating.
              </div>
            )}
          </div>

          {/* Preview result */}
          {summary && (
            <div
              className="border border-zinc-200 p-3"
              data-testid="bulk-card-preview-result"
            >
              <div className="overline mb-2">Validation summary</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 text-xs mb-3">
                <SumCell label="Total rows" value={summary.total_rows} testId="bulk-card-sum-total" />
                <SumCell label="Valid" value={summary.valid_rows} tone="ok" testId="bulk-card-sum-valid" />
                <SumCell label="Invalid" value={summary.invalid_rows} tone={summary.invalid_rows ? "err" : "muted"} testId="bulk-card-sum-invalid" />
                <SumCell label="Workbook dupes" value={summary.workbook_duplicate_count} tone={summary.workbook_duplicate_count ? "warn" : "muted"} testId="bulk-card-sum-wb-dup" />
                <SumCell label="Same-customer dupes" value={summary.existing_duplicate_count} tone={summary.existing_duplicate_count ? "warn" : "muted"} testId="bulk-card-sum-existing-dup" />
                <SumCell label="Cross-customer collisions" value={summary.cross_customer_collision_count} tone={summary.cross_customer_collision_count ? "err" : "muted"} testId="bulk-card-sum-cross-dup" />
              </div>

              <div className="overflow-x-auto border border-zinc-100" style={{ maxHeight: 380 }}>
                <table className="gf-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>NETWORK</th>
                      <th>CARD</th>
                      <th>UNIT</th>
                      <th>DRIVER</th>
                      <th>STATUS</th>
                      <th>ISSUES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr
                        key={r.row_number}
                        style={!r.valid ? { background: "#FEE2E2" } : (r.warnings?.length ? { background: "#FEF9C3" } : {})}
                        data-testid={`bulk-card-preview-row-${r.row_number}`}
                      >
                        <td className="font-mono text-xs">{r.row_number}</td>
                        <td className="font-mono text-xs">{NETWORK_LABEL[r.network] || r.network_raw || "—"}</td>
                        <td className="font-mono">{r.masked_card || "—"}</td>
                        <td className="font-mono text-xs">{r.unit || "—"}</td>
                        <td className="text-xs">{r.driver_name || "—"}</td>
                        <td className="font-mono text-xs">{r.status}</td>
                        <td className="text-xs">
                          {r.errors?.length ? (
                            <ul className="list-disc ml-4">
                              {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                          ) : (
                            <span className="text-emerald-700">OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {preview.rows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-zinc-500 py-4">
                          No data rows found in workbook.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button
            type="button"
            className="gf-btn gf-btn-outline"
            onClick={onClose}
            disabled={committing}
            data-testid="bulk-card-close"
          >
            Close
          </button>
          <button
            type="button"
            className="gf-btn gf-btn-primary"
            onClick={openConfirm}
            disabled={!canCommit}
            data-testid="bulk-card-create-button"
          >
            {canCommit
              ? `Create ${summary.valid_rows} card${summary.valid_rows === 1 ? "" : "s"}`
              : "Create cards"}
          </button>
        </div>
      </div>

      {/* Confirmation dialog — only mounts once eligible + operator clicks */}
      {confirming && (
        <BulkCardConfirmDialog
          companyName={companyName || customerId}
          networkCounts={networkCounts}
          totalValid={summary.valid_rows}
          committing={committing}
          onCancel={closeConfirm}
          onConfirm={submitCommit}
        />
      )}
    </div>
  );
}

function BulkCardConfirmDialog({
  companyName,
  networkCounts,
  totalValid,
  committing,
  onCancel,
  onConfirm,
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={() => !committing && onCancel()}
      data-testid="bulk-card-confirm-dialog"
    >
      <div
        className="bg-white w-full max-w-md border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gf-card-header">
          <h3>
            Create {totalValid} card{totalValid === 1 ? "" : "s"} under{" "}
            <span data-testid="bulk-card-confirm-company">{companyName}</span>?
          </h3>
        </div>
        <div className="gf-card-body space-y-3 text-sm">
          <ul
            className="text-xs font-mono border border-zinc-200 divide-y"
            data-testid="bulk-card-confirm-net-counts"
          >
            {Object.entries(networkCounts).map(([net, n]) => (
              <li
                key={net}
                className="px-2 py-1 flex justify-between"
                data-testid={`bulk-card-confirm-net-${net}`}
              >
                <span>{NETWORK_LABEL[net] || net}</span>
                <span>{n}</span>
              </li>
            ))}
          </ul>
          <div
            className="text-xs text-zinc-600"
            data-testid="bulk-card-confirm-body"
          >
            This will create the cards shown in the validated preview.{" "}
            <b>Transactions will NOT be rematched by this operation.</b>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button
            type="button"
            className="gf-btn gf-btn-outline"
            onClick={onCancel}
            disabled={committing}
            data-testid="bulk-card-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="gf-btn gf-btn-primary"
            onClick={onConfirm}
            disabled={committing}
            data-testid="bulk-card-confirm-submit"
          >
            {committing ? "Creating…" : "Confirm & Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SumCell({ label, value, tone, testId }) {
  const cls =
    tone === "ok" ? "text-emerald-700"
    : tone === "warn" ? "text-amber-700"
    : tone === "err" ? "text-red-700"
    : "text-zinc-700";
  return (
    <div data-testid={testId}>
      <div className="overline text-[10px]">{label}</div>
      <div className={`font-mono text-lg ${cls}`}>{value}</div>
    </div>
  );
}
